import { Link } from "react-router-dom";
import { Star, MapPin, Recycle, ArrowRight, Phone } from "lucide-react";
import { MaskedLine, Reveal, SectionHeading } from "../components/Reveal";
import { Faq } from "../components/Faq";
import { PHONE_DISPLAY, PHONE_TEL, BTN_PRIMARY, BTN_GHOST, IMAGES } from "../data/content";

const FACTS = [
  { icon: Star, label: "4.9/5 from 380+ reviews" },
  { icon: MapPin, label: "Same-day across Dublin & Kildare" },
  { icon: Recycle, label: "96% of waste recycled" },
];

export default function HomePage() {
  return (
    <>
      <section className="mx-auto grid max-w-6xl items-center gap-14 px-5 py-20 sm:px-8 sm:py-28 lg:grid-cols-2">
        <div>
          <h1 className="font-heading text-4xl font-extrabold leading-[1.1] tracking-tight text-ink sm:text-5xl">
            <MaskedLine delay={0.1}>Book Your Skip</MaskedLine>
            <MaskedLine delay={0.25}>
              <span>
                with <span className="text-emerald-700">Easyskip</span>
              </span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.4} className="mt-5">
            <span className="font-mono text-xs font-semibold tracking-[0.3em] text-ink/50">
              FAST. RELIABLE. AFFORDABLE.
            </span>
          </MaskedLine>
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-ink/70">
              A €250 midi skip or a €400 builder's skip — book and pay online in two
              minutes, delivered same-day across Dublin and Kildare.
            </p>
          </Reveal>
          <Reveal delay={0.65}>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/prices" data-testid="hero-book-skip-button" className={BTN_PRIMARY}>
                See prices &amp; book
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a href={PHONE_TEL} data-testid="hero-call-link" className={BTN_GHOST}>
                <Phone className="h-4 w-4" />
                Call {PHONE_DISPLAY}
              </a>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.3}>
          <img
            src={IMAGES.hero}
            alt="Easyskip truck collecting a skip"
            className="h-[360px] w-full rounded-2xl border border-ink/10 object-cover"
          />
        </Reveal>
      </section>

      <section className="border-y border-ink/10">
        <div className="mx-auto grid max-w-6xl gap-5 px-5 py-8 sm:grid-cols-3 sm:px-8">
          {FACTS.map((f) => (
            <Reveal key={f.label}>
              <div className="flex items-center gap-3">
                <f.icon className="h-5 w-5 shrink-0 text-emerald-700" />
                <p className="text-sm font-semibold text-ink">{f.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-b border-ink/10 bg-emerald-700/[0.06]">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:px-8">
          <Reveal>
            <div
              className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between"
              data-testid="areas-highlight"
            >
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-700">
                  Local &amp; proud
                </p>
                <h2 className="mt-2 font-heading text-2xl font-extrabold text-ink sm:text-3xl">
                  Dublin &amp; Kildare only — that's why we're same-day fast.
                </h2>
                <p className="mt-2 max-w-lg text-sm leading-relaxed text-ink/65">
                  We don't spread ourselves across the country. Two counties, our own trucks —
                  order before 11am and your skip lands today.
                </p>
              </div>
              <div className="flex gap-3">
                <span className="rounded-full bg-ink px-5 py-2.5 font-heading text-sm font-bold text-white">
                  Dublin
                </span>
                <span className="rounded-full bg-ink px-5 py-2.5 font-heading text-sm font-bold text-white">
                  Kildare
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 py-16 sm:px-8 sm:py-20">
        <SectionHeading eyebrow="Common questions" title="Good to know." />
        <Reveal>
          <Faq />
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-20 sm:px-8">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-6 rounded-2xl bg-ink px-8 py-12 sm:flex-row sm:items-center sm:px-12">
            <div>
              <h2 className="font-heading text-2xl font-extrabold text-white sm:text-3xl">
                Ready when you are.
              </h2>
              <p className="mt-2 text-sm text-white/70">
                Fixed prices · 5-day hire · 3-tonne limit included.
              </p>
            </div>
            <Link
              to="/prices"
              data-testid="home-cta-book-button"
              className="shrink-0 rounded-full bg-white px-7 py-3.5 font-heading text-sm font-bold text-ink transition-colors duration-300 hover:bg-emerald-100"
            >
              Book a Skip Now
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
